"use client";

import { useEffect, useState } from "react";
import { ShoppingCart, Tag, X, Loader2, CheckCircle2 } from "lucide-react";

import type { UseCartCalculationsReturn } from "@/hooks/useCartCalculations";
import { usePageTranslation } from "@/hooks/usePageTranslation";
import { validateCoupon } from "@/lib/api/business-api";
import type { AppliedCoupon } from "@/store/cart";

interface CartSummaryProps {
  calculations: UseCartCalculationsReturn;
  onCheckout: () => void;
  businessConfig?: any;
  businessId?: string;
  productIds?: string[];
  onCouponApplied?: (coupon: AppliedCoupon) => void;
  onCouponRemoved?: () => void;
  appliedCoupon?: AppliedCoupon | null;
}

/** Last-resort businessId fallback — read directly from localStorage. */
function readBusinessIdFromStorage(): string {
  if (typeof window === "undefined") return "";
  try {
    const raw = localStorage.getItem("business-settings-storage-v2");
    const id = raw ? JSON.parse(raw)?.state?.businessId : "";
    if (id) return id;
    const bd = localStorage.getItem("business_data");
    const parsed = bd ? JSON.parse(bd) : null;
    return parsed?.id || parsed?.businessId || "";
  } catch {
    return "";
  }
}

export function CartSummary({
  calculations,
  onCheckout,
  businessConfig: _businessConfig,
  businessId,
  productIds,
  onCouponApplied,
  onCouponRemoved,
  appliedCoupon,
}: CartSummaryProps) {
  const { t } = usePageTranslation();
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [resolvedBusinessId, setResolvedBusinessId] = useState<string>(businessId || "");

  // After mount, fall back to localStorage if the prop was empty.
  useEffect(() => {
    if (businessId) {
      setResolvedBusinessId(businessId);
      return;
    }
    const fromLs = readBusinessIdFromStorage();
    if (fromLs) setResolvedBusinessId(fromLs);
  }, [businessId]);

  const effectiveBusinessId = businessId || resolvedBusinessId;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim() || !effectiveBusinessId) return;
    setCouponLoading(true);
    setCouponError("");
    try {
      const result = await validateCoupon({
        code: couponCode.trim(),
        businessId: effectiveBusinessId,
        cartTotal: calculations.subtotal,
        itemCount: calculations.itemCount,
        productIds,
      });

      if (result.valid) {
        onCouponApplied?.({
          code: result.code,
          discount: result.discountAmount || 0,
          discountType: result.discountType,
          description: result.description,
        });
        setCouponCode("");
      } else {
        setCouponError(result.error || t("cart.invalidCoupon"));
      }
    } catch {
      setCouponError(t("cart.couponValidationFailed"));
    } finally {
      setCouponLoading(false);
    }
  };

  return (
    <div className="cart-summary rounded-brand border border-brand-border bg-brand-surface p-3 sm:p-6 shadow-lg">
      <h2 className="mb-4 sm:mb-6 flex items-center gap-2 text-lg sm:text-xl font-bold text-brand-fg">
        <ShoppingCart className="h-5 w-5 text-brand-primary" />
        {t("cart.orderSummary")}
      </h2>

      {/* Coupon Code Input */}
      {effectiveBusinessId && (
        <div className="cart-summary__coupon mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-brand-border">
          {appliedCoupon ? (
            <div className="flex items-center justify-between rounded-brand-sm bg-brand-success/10 border border-brand-success/25 p-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-brand-success" />
                <div>
                  <span className="text-sm font-medium text-brand-success">
                    {appliedCoupon.code}
                  </span>
                  {appliedCoupon.description && (
                    <p className="text-xs text-brand-success">
                      {appliedCoupon.description}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={onCouponRemoved}
                className="text-brand-success hover:text-brand-success/80 p-1"
                aria-label={t("cart.removeCoupon")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div>
              <label className="text-sm font-medium text-brand-fg mb-2 block">
                {t("cart.haveCoupon")}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(
                      e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "")
                    );
                    setCouponError("");
                  }}
                  maxLength={32}
                  placeholder={t("cart.enterCode")}
                  className="flex-1 rounded-brand-sm border border-brand-border bg-brand-bg px-3 py-2 text-sm focus:border-brand-ring focus:outline-none focus:ring-1 focus:ring-brand-ring"
                  onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                />
                <button
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponCode.trim()}
                  className="rounded-brand-sm bg-brand-primary px-4 py-2 text-sm font-medium text-brand-primary-foreground hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {couponLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t("cart.apply")
                  )}
                </button>
              </div>
              {couponError && (
                <p className="mt-1 text-xs text-brand-danger">{couponError}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Price Breakdown */}
      <div className="cart-summary__breakdown space-y-2 sm:space-y-3 border-b border-brand-border pb-4 sm:pb-6 text-sm sm:text-base">
        <div className="flex justify-between text-brand-fg">
          <span>{t("cart.subtotalItems", { count: calculations.itemCount })}</span>
          <span className="font-medium">{calculations.formattedSubtotal}</span>
        </div>

        {calculations.discount > 0 && (
          <div className="flex justify-between text-brand-success">
            <span className="flex items-center gap-1">
              <Tag className="h-4 w-4" />
              {t("cart.discount")}
            </span>
            <span className="font-medium">
              -{calculations.formattedDiscount}
            </span>
          </div>
        )}

        {calculations.couponDiscount > 0 && (
          <div className="flex justify-between text-brand-success">
            <span className="flex items-center gap-1">
              <Tag className="h-4 w-4" />
              {t("cart.coupon")}
            </span>
            <span className="font-medium">
              -{calculations.formattedCouponDiscount}
            </span>
          </div>
        )}

        <div className="flex justify-between text-brand-fg">
          <span>{t("cart.tax")}</span>
          <span className="font-medium">{calculations.formattedTax}</span>
        </div>
      </div>

      {/* Total */}
      <div className="cart-summary__total mt-4 mb-4 sm:mt-6 sm:mb-6">
        <div className="flex items-center justify-between">
          <span className="text-base sm:text-lg font-semibold text-brand-fg">
            {t("cart.total")}
          </span>
          <span className="text-xl sm:text-2xl font-bold text-brand-primary">
            {calculations.formattedTotal}
          </span>
        </div>
      </div>

      {/* Checkout Button */}
      <button
        onClick={onCheckout}
        className="cart-summary__checkout-btn flex w-full items-center justify-center gap-2 rounded-brand bg-brand-primary px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-semibold text-brand-primary-foreground shadow-md transition-colors hover:bg-brand-primary-hover hover:shadow-lg"
      >
        <ShoppingCart className="h-5 w-5" />
        {t("cart.checkout")}
      </button>

      {/* Additional Info */}
      <div className="cart-summary__info mt-3 sm:mt-4 text-center">
        <p className="text-xs text-brand-muted">
          {t("cart.shippingAtCheckout")}
        </p>
      </div>
    </div>
  );
}
